import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL, buildApiUrl } from '../config/api';
import { apiErrorMessage } from './prescriptionForm';
import {
  FEED_STATE,
  ROTATE_CONFIRM,
  SUBSCRIPTION_NOTE,
  TURN_OFF_CONFIRM,
  feedStateFrom,
  feedUrl,
  issuingReplaces,
} from './calendarSubscription';

/**
 * "Subscribe to your training calendar": the athlete's .ics feed link.
 *
 * Rendered on the account card and on the athlete's own home, because an
 * athlete without a roster never sees the account card — and athletes are who
 * the feed is for.
 *
 * # WHY THE LINK IS SHOWN ONLY ONCE
 *
 * The server stores a hash of the token, never the token, so it cannot hand
 * the link back later — and a link anybody could re-read from this screen would
 * be one more place to steal it from. After a reload an active feed reads as
 * "active", and getting the link again means rotating it, which retires the
 * old one.
 */
const CalendarSubscriptionCard = () => {
  const { token } = useAuth();

  const [state, setState] = useState(FEED_STATE.LOADING);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(buildApiUrl('/users/calendar-token'), authConfig());
        if (!cancelled) setState(feedStateFrom(response?.data));
      } catch {
        if (!cancelled) setState(FEED_STATE.UNKNOWN);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authConfig]);

  const issue = async () => {
    const replacing = issuingReplaces(state);
    // Confirmed whenever a live link might exist, including when the status
    // could not be read: rotating kills every subscribed calendar at once.
    if (replacing && !window.confirm(ROTATE_CONFIRM)) return;

    setBusy(true);
    setStatus(null);
    try {
      const response = await axios.post(buildApiUrl('/users/calendar-token'), null, authConfig());
      setUrl(feedUrl(response?.data?.path, API_URL, window.location.origin));
      setState(FEED_STATE.ON);
      setStatus({
        tone: 'success',
        message: replacing
          ? 'New link created. Any previous link no longer works.'
          : 'Link created. Add it to your calendar app.',
      });
    } catch (error) {
      setStatus({ tone: 'error', message: apiErrorMessage(error, 'Could not create the link.') });
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    if (!window.confirm(TURN_OFF_CONFIRM)) return;
    setBusy(true);
    setStatus(null);
    try {
      await axios.delete(buildApiUrl('/users/calendar-token'), authConfig());
      setUrl('');
      setState(FEED_STATE.OFF);
      setStatus({ tone: 'success', message: 'Calendar link turned off. It no longer works.' });
    } catch (error) {
      setStatus({ tone: 'error', message: apiErrorMessage(error, 'Could not turn the link off.') });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setStatus({ tone: 'success', message: 'Link copied.' });
    } catch {
      // Clipboard access is refused outside a secure context and by some
      // browsers' settings. The link is on screen in a selectable field, so say
      // that rather than pretend.
      setStatus({ tone: 'error', message: 'Could not copy — select the link and copy it yourself.' });
    }
  };

  const issueLabel = {
    [FEED_STATE.OFF]: 'Subscribe to your training calendar',
    [FEED_STATE.ON]: 'Rotate link',
    [FEED_STATE.UNKNOWN]: 'Create a new link',
    [FEED_STATE.LOADING]: 'Subscribe to your training calendar',
  }[state];

  return (
    <section className="calendar-subscription" aria-labelledby="calendar-subscription-heading">
      <h3 className="kicker" id="calendar-subscription-heading">
        Training calendar
      </h3>
      <p className="meta-copy calendar-subscription-note">{SUBSCRIPTION_NOTE}</p>

      {url && (
        <>
          <a className="calendar-subscription-link" href={url}>
            Subscribe to your training calendar
          </a>
          <p className="meta-copy">
            Copy this link and paste it into your calendar app's "Subscribe to calendar" or "From URL" option.
          </p>
          <label className="field-label" htmlFor="calendar-feed-url">
            Calendar link
            <input
              id="calendar-feed-url"
              className="field"
              readOnly
              value={url}
              onFocus={(event) => event.target.select()}
            />
          </label>
        </>
      )}

      {state === FEED_STATE.ON && !url && (
        <p className="meta-copy">
          Your calendar link is active. It is shown only once, when it is created — rotate it
          to get a new one.
        </p>
      )}

      {state === FEED_STATE.UNKNOWN && (
        <p className="meta-copy">
          Could not check whether you already have a calendar link. Creating one replaces any
          link you have.
        </p>
      )}

      <div className="calendar-subscription-actions">
        {url && (
          <button type="button" className="btn-ghost" onClick={copy}>
            Copy link
          </button>
        )}
        <button
          type="button"
          className={state === FEED_STATE.OFF ? 'btn-primary' : 'btn-ghost'}
          onClick={issue}
          // Not before the status is known: until then "create" might be a
          // rotation, and the label could not say which.
          disabled={busy || state === FEED_STATE.LOADING}
        >
          {issueLabel}
        </button>
        {state === FEED_STATE.ON && (
          <button type="button" className="btn-ghost" onClick={turnOff} disabled={busy}>
            Turn off link
          </button>
        )}
      </div>

      {status && (
        <p className={status.tone === 'error' ? 'status-error' : 'status-success'} role="status">
          {status.message}
        </p>
      )}
    </section>
  );
};

export default CalendarSubscriptionCard;
